import {
  ArrowDownIcon,
  Check,
  Download,
  Edit2,
  Eye,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppButton from "../../components/ui/AppButton";
import DashboardFilterBar, {
  ALL_OPTION,
  isAllFilter,
  stringFromFilter,
} from "../../components/ui/dashboard/DashboardFilterBar";
import Pagination from "../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../components/ui/dashboard/Table";
import ThreeDotsMenu from "../../components/ui/ThreeDotsMenu";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { usePermissions } from "../../hooks/usePermissions";
import { cn, formatCurrencyForTable } from "../../lib/utils";
import {
  Requisition,
  RequisitionSearchRequest,
  RequisitionSearchResponse,
} from "./@types";
import ApproveDeclineRequisitionModal from "./ApproveDeclineRequisitionModal";
import {
  useGetApprovalHistoryRequisitionsMutation,
  useGetRequisitionsMutation,
  useGetReviewRequisitionsMutation,
} from "./requisitionApiSlice";
import RequisitionDetailsModal from "./RequisitionDetailsModal";
import RequisitionModal from "./RequisitionModal";

type TabType = "record" | "reviews" | "approval-history";

const REQUISITION_STATUS_OPTIONS = [
  ALL_OPTION,
  { value: "Pending", label: "Pending" },
  { value: "Approved", label: "Approved" },
  { value: "Decline", label: "Declined" },
];

const isRequisitionTab = (value: string | null): value is TabType =>
  value === "record" || value === "reviews" || value === "approval-history";

const RequisitionDash: React.FC = () => {
  const {
    canCreate,
    canUpdate,
    canDelete,
    canExport,
    canApprove,
    canDecline,
  } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const [open, setOpen] = React.useState(false);
  const [openModal, setOpenModal] = useState("");
  const [activeTab, setActiveTab] = useState<TabType>(
    isRequisitionTab(tabFromUrl) ? tabFromUrl : "record"
  );
  const [selectedRequisitionIds, setSelectedRequisitionIds] = useState<
    Set<string>
  >(new Set());
  const [approveDeclineModalOpen, setApproveDeclineModalOpen] = useState(false);
  const [approveDeclineAction, setApproveDeclineAction] = useState<
    boolean | null
  >(null);

  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const debouncedKeyword = useDebouncedValue(searchTerm.trim());

  const [selectedRequisition, setSelectedRequisition] =
    useState<Requisition | null>(null);

  // RTK Query mutations
  const [getRequisition, { data: driverData, isLoading, error: recordError }] =
    useGetRequisitionsMutation();
  const [
    getReviewRequisitions,
    {
      data: reviewData,
      isLoading: isReviewLoading,
      error: reviewError,
    },
  ] = useGetReviewRequisitionsMutation();
  const [
    getApprovalHistoryRequisitions,
    {
      data: approvalHistoryData,
      isLoading: isApprovalHistoryLoading,
      error: approvalHistoryError,
    },
  ] = useGetApprovalHistoryRequisitionsMutation();

  const fetchCM = useCallback(async () => {
    const requestBody: RequisitionSearchRequest = {
      pageNumber: currentPage,
      pageSize,
      keyword: debouncedKeyword || undefined,
      status: stringFromFilter(statusFilter),
    };

    try {
      if (activeTab === "record") {
        await getRequisition(requestBody).unwrap();
      } else if (activeTab === "reviews") {
        await getReviewRequisitions(requestBody).unwrap();
      } else if (activeTab === "approval-history") {
        await getApprovalHistoryRequisitions(requestBody).unwrap();
      }
    } catch (err) {
      console.error("Failed to fetch requisitions:", err);
    }
  }, [
    currentPage,
    pageSize,
    debouncedKeyword,
    statusFilter,
    activeTab,
    getRequisition,
    getReviewRequisitions,
    getApprovalHistoryRequisitions,
  ]);

  // Fetch requisitions on component mount and when filters change
  useEffect(() => {
    fetchCM();
    setSelectedRequisitionIds(new Set());
  }, [fetchCM, activeTab]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedKeyword, statusFilter, activeTab]);

  useEffect(() => {
    if (isRequisitionTab(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Determine which data source to use based on active tab
  const getCurrentData = () => {
    if (activeTab === "reviews") {
      return reviewData?.data as RequisitionSearchResponse | undefined;
    } else if (activeTab === "approval-history") {
      return approvalHistoryData?.data as RequisitionSearchResponse | undefined;
    }
    return driverData?.data as RequisitionSearchResponse | undefined;
  };

  const isLoadingCurrentTab =
    activeTab === "reviews"
      ? isReviewLoading
      : activeTab === "approval-history"
      ? isApprovalHistoryLoading
      : isLoading;

  const currentTabError =
    activeTab === "reviews"
      ? reviewError
      : activeTab === "approval-history"
      ? approvalHistoryError
      : recordError;

  // Handle checkbox selection
  const handleSelectRequisition = (requisitionId: string) => {
    const newSelected = new Set(selectedRequisitionIds);
    if (newSelected.has(requisitionId)) {
      newSelected.delete(requisitionId);
    } else {
      newSelected.add(requisitionId);
    }
    setSelectedRequisitionIds(newSelected);
  };

  const handleSelectAll = () => {
    const currentData = getCurrentData();
    if (!currentData?.data) return;

    if (selectedRequisitionIds.size === currentData.data.length) {
      setSelectedRequisitionIds(new Set());
    } else {
      setSelectedRequisitionIds(new Set(currentData.data.map((req) => req.id)));
    }
  };

  const getSelectedRequisitions = (): Requisition[] => {
    const currentData = getCurrentData();
    if (!currentData?.data) return [];
    return currentData.data.filter((req) => selectedRequisitionIds.has(req.id));
  };

  const handleApproveClick = () => {
    if (selectedRequisitionIds.size > 0) {
      setApproveDeclineAction(true);
      setApproveDeclineModalOpen(true);
    }
  };

  const handleDeclineClick = () => {
    if (selectedRequisitionIds.size > 0) {
      setApproveDeclineAction(false);
      setApproveDeclineModalOpen(true);
    }
  };

  const handleApproveDeclineSuccess = () => {
    setApproveDeclineModalOpen(false);
    setSelectedRequisitionIds(new Set());
    fetchCM();
  };

  // Table columns configuration
  const showCheckboxColumn =
    activeTab === "reviews" || activeTab === "approval-history";
  const currentData = getCurrentData();
  const activeFilters = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];
    if (debouncedKeyword) {
      chips.push({
        key: "keyword",
        label: `Search: ${debouncedKeyword}`,
        onRemove: () => setSearchTerm(""),
      });
    }
    if (!isAllFilter(statusFilter)) {
      chips.push({
        key: "status",
        label: `Status: ${REQUISITION_STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label}`,
        onRemove: () => setStatusFilter("all"),
      });
    }
    return chips;
  }, [debouncedKeyword, statusFilter]);

  const columns: Column<Requisition>[] = [
    ...(showCheckboxColumn
      ? [
          {
            key: "checkbox",
            header: (
              <input
                type="checkbox"
                checked={
                  currentData?.data &&
                  selectedRequisitionIds.size === currentData.data.length &&
                  currentData.data.length > 0
                }
                onChange={handleSelectAll}
                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
            ),
            width: "50px",
            align: "center" as const,
            render: (value: any, item: Requisition) => (
              <input
                type="checkbox"
                checked={selectedRequisitionIds.has(item.id)}
                onChange={() => handleSelectRequisition(item.id)}
                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
            ),
          },
        ]
      : []),
    {
      key: "requisitionNumber",
      header: "ID",
      width: "",
      render: (value: string) => (
        <span className="text-hColor capitalize">{value}</span>
      ),
    },
    {
      key: "details",
      header: "Details",

      render: (value: string, item: Requisition) => {
        return (
          <div>
            <div
              onClick={() => setOpen(!open)}
              className="flex items-center p-2 border border-[#BDBDBD] rounded-md bg-gray-100 cursor-pointer"
            >
              Show item details <ArrowDownIcon className="w-4 h-4 ml-1" />
            </div>

            {open && (
              <div className="mt-2 space-y-1">
                <p>{item.bankAccountNumber}</p>

                {item.items.map((sub, index) => (
                  <p key={index}>{sub.description}</p>
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "cardId",
      header: "Card ID",

      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "description",
      header: "Description",

      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "totalAmount",
      header: "Amt (NGN)",

      render: (value: number) => (
        <span className="text-hColor">{formatCurrencyForTable(value)}</span>
      ),
    },
    {
      key: "payTo",
      header: "Pay To",

      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "bankName",
      header: "Bank",

      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "status",
      header: "Status",

      render: (value: boolean, item: Requisition) => {
        const getStatusInfo = () => {
          if (!value)
            return {
              label: "Pending",
              color: "text-yellow-500 border border-yellow-500",
            };
          if (item.status === "Approved")
            return {
              label: "Approved",
              color: "text-green-800 border border-green-800",
            };
          if (item.status === "Decline")
            return {
              label: "Decline",
              color: "text-red-800 border border-red-800",
            };
          return {
            label: "Pending",
            color: "text-yellow-500 border border-yellow-500",
          };
        };

        const status = getStatusInfo();
        return (
          <span
            className={`w-[60px] inline-flex items-center justify-center px-2 py-1 text-xs font-semibold rounded-full ${status.color}`}
          >
            {status.label}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "Action",

      align: "center" as const,
      render: (value: any, item: Requisition) => {
        return (
          <ThreeDotsMenu
            actions={[
              {
                label: "View",
                onClick: () => {
                  setSelectedRequisition(item);
                  setOpenModal("details");
                },
                icon: <Eye className="w-4 h-4" />,
              },
              ...(canUpdate("Requisition")
                ? [
                    {
                      label: "Edit",
                      onClick: () => {
                        setSelectedRequisition(item);
                        setOpenModal("edit");
                      },
                      icon: <Edit2 className="w-4 h-4" />,
                    },
                  ]
                : []),
              ...(canDelete("Requisition")
                ? [
                    {
                      label: "Delete",
                      onClick: () => {
                        setSelectedRequisition(item);
                        setOpenModal("delete");
                      },
                      icon: <Trash2 className="w-4 h-4" />,
                    },
                  ]
                : []),
            ]}
          />
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleModal = () => {
    setOpenModal("");
    fetchCM();
  };

  const handleExportCSV = () => {
    console.log("Export CSV functionality");
  };

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-[10px] p-4">
          <div className="">
            <h2 className="text-xl font-semibold text-hColor">Requisition</h2>
          </div>

          <div className="flex gap-3">
            {canExport("Requisition") && (
              <AppButton
                appearance="soft"
                onClick={handleExportCSV}
                className="flex items-center px-4 py-2 border border-primary bg-white rounded-lg text-primary hover:bg-gray-50 transition-colors min-w-[146px] h-[38px]"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </AppButton>
            )}
            {canCreate("Requisition") && (
              <AppButton
                onClick={() => setOpenModal("add")}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/60 transition-colors h-[38px]"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add requisition
              </AppButton>
            )}
          </div>
        </div>

        {/* Requisition section */}
        <div className="space-y-6 bg-white rounded-[10px] p-4">
          {/* Filters and Actions */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  setActiveTab("record");
                  setSearchParams({}, { replace: true });
                }}
                className={cn(
                  "rounded-md py-2 px-4 transition-colors",
                  activeTab === "record"
                    ? "bg-primary/10 text-primary"
                    : "text-gray-600 hover:text-primary"
                )}
              >
                Record
              </button>
              <button
                onClick={() => {
                  setActiveTab("reviews");
                  setSearchParams({ tab: "reviews" }, { replace: true });
                }}
                className={cn(
                  "rounded-md py-2 px-4 transition-colors",
                  activeTab === "reviews"
                    ? "bg-primary/10 text-primary"
                    : "text-gray-600 hover:text-primary"
                )}
              >
                Reviews
              </button>
              <button
                onClick={() => {
                  setActiveTab("approval-history");
                  setSearchParams(
                    { tab: "approval-history" },
                    { replace: true }
                  );
                }}
                className={cn(
                  "rounded-md py-2 px-4 transition-colors",
                  activeTab === "approval-history"
                    ? "bg-primary/10 text-primary"
                    : "text-gray-600 hover:text-primary"
                )}
              >
                Approval history
              </button>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 items-center">
              {/* Approve/Decline buttons - only show on Reviews and Approval history tabs when items are selected */}
              {(activeTab === "reviews" || activeTab === "approval-history") &&
                selectedRequisitionIds.size > 0 && (
                  <div className="flex gap-2">
                    {canApprove("Requisition") && (
                      <AppButton
                        onClick={handleApproveClick}
                        className="flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors h-[38px]"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Approve Selected
                      </AppButton>
                    )}
                    {canDecline("Requisition") && (
                      <AppButton
                        onClick={handleDeclineClick}
                        className="flex items-center px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors h-[38px]"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Decline Selected
                      </AppButton>
                    )}
                  </div>
                )}

              <DashboardFilterBar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onClearSearch={() => setSearchTerm("")}
                searchPlaceholder="Search requisitions"
                isLoading={isLoadingCurrentTab}
                filters={[
                  {
                    id: "status",
                    placeholder: "Status",
                    value: statusFilter,
                    options: REQUISITION_STATUS_OPTIONS,
                    onChange: (value) => setStatusFilter(value || "all"),
                  },
                ]}
                chips={activeFilters}
                onClearAll={() => {
                  setSearchTerm("");
                  setStatusFilter("all");
                }}
                resultCount={currentData?.totalCount}
                resultLabel={
                  currentData?.totalCount === 1
                    ? "requisition"
                    : "requisitions"
                }
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-hidden space-y-6">
            <Table
              data={currentData?.data || []}
              columns={columns}
              loading={isLoadingCurrentTab}
              error={currentTabError}
              emptyMessage="No requisitions found"
            />
          </div>
        </div>

        <div className="">
          {/* Pagination */}
          {currentData && (
            <Pagination
              currentPage={currentData.currentPage}
              totalPages={currentData.totalPages}
              pageSize={currentData.pageSize}
              totalCount={currentData.totalCount}
              hasNextPage={currentData.hasNextPage}
              hasPreviousPage={currentData.hasPreviousPage}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </div>
      </div>

      <RequisitionModal
        isOpen={openModal === "add" || openModal === "edit" ? true : false}
        onClose={() => handleModal()}
        requisition={selectedRequisition ? selectedRequisition : null}
      />

      {selectedRequisition && (
        <RequisitionDetailsModal
          isOpen={openModal === "details" ? true : false}
          onClose={() => handleModal()}
          requisition={selectedRequisition}
        />
      )}

      {approveDeclineAction !== null && (
        <ApproveDeclineRequisitionModal
          requisitions={getSelectedRequisitions()}
          isApproved={approveDeclineAction}
          isOpen={approveDeclineModalOpen}
          onClose={() => {
            setApproveDeclineModalOpen(false);
            setApproveDeclineAction(null);
          }}
          onSuccess={handleApproveDeclineSuccess}
        />
      )}
    </div>
  );
};

export default RequisitionDash;
